import React from 'react';

import {NucleotideType} from 'biojs/Util';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from 'molbio/monomers/__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {
  displayAsNatural,
  getDisplayModsForSlice,
  getVisualSymbolForPhosphateMonomer,
  letterCenterX,
} from 'molbio/monomers/ModificationVisualizationUtil';
import ModifiedBase from 'molbio/monomers/ModifiedBase';
import ModifiedPhosphate from 'molbio/monomers/ModifiedPhosphate';
import ModifiedSugar from 'molbio/monomers/ModifiedSugar';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  MONOMER_QUERY,
  UNMODIFIED_BASE_COLOR,
} from 'molbio/monomers/MonomerConstants';
import {MODIFIED_NUCLEOTIDES_QUERY} from 'molbio/monomers/NucleotidesTab';
import Popover from 'ui-library/floating-ui/Popover';

import {ModifiedNucleotideGraphQL} from '../../../../graphql-fragments/modbio/__generated/ModifiedNucleotideGraphQL';
import ComponentWrapper from './ComponentWrapper';
import {widthBetweenLetters} from './LinearMapUtil';
import ModifiedNucleotideTooltipContents from './ModifiedNucleotideTooltipContents';

type Monomer = MonomerQuery['monomers'][number];

const UNMODIFIED_FILL_OPACITY = 0.05;
const MODIFIED_FILL_OPACITY = 0.0001;
// Arbitrary widthBetweenLetters at which we make the circles smaller to look less cluttered
const SHIFT_THRESHOLD = 28;
export const MODIFIED_NUCLEOTIDES_BOX_HEIGHT = 10;
const LETTER_CENTER_Y_OFFSET = MODIFIED_NUCLEOTIDES_BOX_HEIGHT - 2;

// The width of a modified phosphate
export const PHOSPHATE_WIDTH = 3;
// The height of a modified phosphate
export const PHOSPHATE_HEIGHT = 3;

function letterCenterY(xFn: (index: number) => number, sliceStart: number, boxHeightToDNA: number): number {
  return LETTER_CENTER_Y_OFFSET + boxHeightToDNA + 0.5 * widthBetweenLetters(xFn, sliceStart);
}

function phosphateSymbolOffsetX(
  xFn: (index: number) => number,
  sliceStart: number,
  isForwardStrand: boolean
): number {
  const forwardPhosphateOffset = 0.25 * widthBetweenLetters(xFn, sliceStart) + 2;
  return isForwardStrand
    ? forwardPhosphateOffset
    : -forwardPhosphateOffset - phosphateSymbolSize(xFn, sliceStart);
}

function phosphateSymbolOffsetY(
  xFn: (index: number) => number,
  sliceStart: number,
  isForwardStrand: boolean
): number {
  const reversePhosphateOffset = 0.125 * widthBetweenLetters(xFn, sliceStart) + LETTER_CENTER_Y_OFFSET;
  return isForwardStrand ? -reversePhosphateOffset - 4 : reversePhosphateOffset - 4;
}

function phosphateSymbolSize(xFn: (index: number) => number, sliceStart: number): number {
  return 0.25 * widthBetweenLetters(xFn, sliceStart);
}

function outerRadiusRatio(xFn: (index: number) => number, sliceStart: number): number {
  return widthBetweenLetters(xFn, sliceStart) < SHIFT_THRESHOLD ? 0.45 : 0.4;
}

function innerRadiusRatio(xFn: (index: number) => number, sliceStart: number): number {
  return widthBetweenLetters(xFn, sliceStart) < SHIFT_THRESHOLD ? 0.35 : 0.3;
}

function outerRadius(xFn: (index: number) => number, sliceStart: number): number {
  return outerRadiusRatio(xFn, sliceStart) * widthBetweenLetters(xFn, sliceStart);
}

export function modifiedNucleotideVisualizationHeight(
  xFn: (index: number) => number,
  sliceStart: number,
  boxHeightToDNA: number
): number {
  // The center of the base is positioned here, relative to the top of the container element
  const baseCenterYPosition = letterCenterY(xFn, sliceStart, boxHeightToDNA);
  // The top of the base is 'r' away from the center of the base
  const baseRadius = outerRadius(xFn, sliceStart);
  const baseTopYPosition = baseCenterYPosition - baseRadius;

  // The total height of the element containing the base and vertical whitespace is the position of the top
  // of the base + the height of the base
  return baseTopYPosition + baseRadius * 2;
}

export function modifiedNucleotideVMargin(
  xFn: (index: number) => number,
  sliceStart: number,
  boxHeightToDNA: number
): number {
  // The center of the base is positioned here relative to the top of the container element
  const baseCenterYPosition = letterCenterY(xFn, sliceStart, boxHeightToDNA);
  // The top of the base is 'r' away from the center of the base
  // The distance from the top of the container element to the top of the base is the vertical whitespace
  const baseRadius = outerRadius(xFn, sliceStart);
  return baseCenterYPosition - baseRadius;
}

interface SharedModifiedNucleotidesPropsInterface {
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  bases: string;
  boxHeightToDNA: number;
  showNucleotideStructures: boolean;
}

export interface SummarizedModifiedNucleotideTooltipsProps extends SharedModifiedNucleotidesPropsInterface {
  sequenceMods: Array<ModifiedNucleotideAttributes>;
  monomers: Array<Monomer>;
  nucleotideType: NucleotideType;
  isOligo: boolean;
  keyPrefix: string;
}

export function SummarizedModifiedNucleotideTooltips({
  xFn,
  sliceStart,
  sliceEnd,
  keyPrefix,
  sequenceMods,
  monomers,
  bases,
  nucleotideType,
  boxHeightToDNA,
  showNucleotideStructures,
  isOligo,
}: SummarizedModifiedNucleotideTooltipsProps): JSX.Element {
  function getCircleFillOpacity(slicePosition: number): number {
    if (sequenceMods.map((mod: ModifiedNucleotideAttributes) => mod.position).includes(slicePosition)) {
      // Hack: if there is a modified base or sugar, then we do not want the lightly shaded gray circle.
      // However, if there is no fill to the circle, the tooltip will not appear over it, so we fill it in
      // with trivial fill opacity.
      const mod = sequenceMods.filter((mod) => mod.position === slicePosition)[0];
      if (
        !displayAsNatural(mod.baseMonomerId, monomers, nucleotideType, isOligo) ||
        !displayAsNatural(mod.sugarMonomerId, monomers, nucleotideType, isOligo)
      ) {
        return MODIFIED_FILL_OPACITY;
      }
    }
    return UNMODIFIED_FILL_OPACITY;
  }

  const sliceArray = [];
  for (let i = sliceStart; i < sliceEnd; i++) {
    sliceArray.push(i);
  }

  return (
    <svg>
      {sliceArray.map((slicePosition: number, i: number) => (
        // Put a lightly shaded circle for the Popover to appear over.
        <Popover
          key={`${keyPrefix}-${slicePosition}`}
          renderContent={() => {
            const mod: ModifiedNucleotideAttributes | null = sequenceMods.filter(
              (mod) => mod.position === slicePosition
            )[0];
            return (
              <ModifiedNucleotideTooltipContents
                mod={mod}
                monomers={monomers}
                base={bases[i]}
                nucleotideType={nucleotideType}
                showNucleotideStructure={showNucleotideStructures}
              />
            );
          }}
        >
          <g>
            <circle
              cx={letterCenterX(xFn, sliceStart, slicePosition)}
              cy={letterCenterY(xFn, sliceStart, boxHeightToDNA)}
              r={outerRadius(xFn, sliceStart)}
              fill={UNMODIFIED_BASE_COLOR}
              fillOpacity={getCircleFillOpacity(slicePosition)}
            />
          </g>
        </Popover>
      ))}
    </svg>
  );
}

export interface ModifiedNucleotidesVisualizationProps extends SharedModifiedNucleotidesPropsInterface {
  layout: {vMargin: number; visible?: boolean};
  isOligo: boolean;
  sequenceMods: Array<ModifiedNucleotideAttributes>;
  monomers: Array<Monomer>;
  nucleotideType: NucleotideType;
  keyPrefix: string;
  // Set to false if on the reverse strand of an oligo duplex.
  isForwardStrand?: boolean;
  // Whether component is wrapped in G element, as opposed to SVG element. Returns as SVG if undefined.
  shouldReturnG?: boolean;
  displayBasesAsNatural?: boolean;
}

export function ModifiedNucleotidesVisualization({
  isOligo,
  sequenceMods,
  monomers,
  nucleotideType,
  xFn,
  sliceStart,
  sliceEnd,
  keyPrefix,
  bases,
  layout,
  boxHeightToDNA,
  showNucleotideStructures,
  isForwardStrand,
  shouldReturnG,
  displayBasesAsNatural,
}: ModifiedNucleotidesVisualizationProps): JSX.Element {
  const displayMods = getDisplayModsForSlice(monomers, nucleotideType, bases, sequenceMods, sliceStart);
  const modifiedNucleotidesVisualization = (
    <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
      <g>
        {displayMods
          .filter((mod: ModifiedNucleotideGraphQL) => mod.position >= sliceStart && mod.position < sliceEnd)
          .map((mod: ModifiedNucleotideGraphQL) => {
            const baseMonomer = monomers.find((monomer) => monomer.id === mod.baseMonomerId);
            const displayBaseNatural =
              (displayBasesAsNatural as boolean) ||
              displayAsNatural(mod.baseMonomerId, monomers, nucleotideType, isOligo);
            const sugarMonomer = monomers.find((monomer) => monomer.id === mod.sugarMonomerId);
            const displaySugarNatural = displayAsNatural(
              mod.sugarMonomerId,
              monomers,
              nucleotideType,
              isOligo
            );
            const phosphateMonomer = monomers.find((monomer) => monomer.id === mod.phosphateMonomerId);
            const displayPhosphateNatural = displayAsNatural(
              mod.phosphateMonomerId,
              monomers,
              nucleotideType,
              isOligo
            );
            return (
              <g
                key={`${mod.position}-${mod.sugarMonomerId}-${mod.baseMonomerId}-${mod.phosphateMonomerId}`}
                transform={`translate(${letterCenterX(xFn, sliceStart, mod.position)},
            ${letterCenterY(xFn, sliceStart, boxHeightToDNA)})`}
              >
                {!displayBaseNatural && (
                  <ModifiedBase
                    fill={baseMonomer?.color ?? DEFAULT_MODIFIED_BASE_COLOR}
                    // If there is also a modified sugar, make modified base circle smaller to
                    // accommodate. Otherwise, make modified base circle same size as modified sugar
                    // circle.
                    radius={
                      !displaySugarNatural
                        ? innerRadiusRatio(xFn, sliceStart) * widthBetweenLetters(xFn, sliceStart)
                        : outerRadius(xFn, sliceStart)
                    }
                  />
                )}
                {!displaySugarNatural && (
                  <ModifiedSugar
                    radius={outerRadius(xFn, sliceStart)}
                    stroke={sugarMonomer?.color ?? DEFAULT_MODIFIED_SUGAR_COLOR}
                  />
                )}
                {!displayPhosphateNatural && (
                  <ModifiedPhosphate
                    size={phosphateSymbolSize(xFn, sliceStart)}
                    offsetX={phosphateSymbolOffsetX(xFn, sliceStart, isForwardStrand ?? true)}
                    offsetY={phosphateSymbolOffsetY(xFn, sliceStart, isForwardStrand ?? true)}
                    symbol={getVisualSymbolForPhosphateMonomer(phosphateMonomer)}
                  />
                )}
              </g>
            );
          })}
        <SummarizedModifiedNucleotideTooltips
          xFn={xFn}
          sliceStart={sliceStart}
          sliceEnd={sliceEnd}
          keyPrefix={keyPrefix}
          sequenceMods={displayMods}
          monomers={monomers}
          bases={bases}
          nucleotideType={nucleotideType}
          boxHeightToDNA={boxHeightToDNA}
          showNucleotideStructures={showNucleotideStructures}
          isOligo={isOligo}
        />
      </g>
    </ComponentWrapper>
  );
  return shouldReturnG ? modifiedNucleotidesVisualization : <svg>{modifiedNucleotidesVisualization}</svg>;
}

export interface ModifiedNucleotidesProps extends SharedModifiedNucleotidesPropsInterface {
  layout: AllPolymerLayouts['modifiedNucleotides'];
  sequenceId: string;
}

export default function ModifiedNucleotides({
  xFn,
  sliceStart,
  sliceEnd,
  sequenceId,
  bases,
  layout,
  boxHeightToDNA,
  showNucleotideStructures,
}: ModifiedNucleotidesProps): JSX.Element | null {
  if (!layout.visible) {
    return null;
  }
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {(monomerData) => (
        // Hack: fetch monomers first to populate the cache, to make MODIFIED_NUCLEOTIDES_QUERY work
        <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
          query={MODIFIED_NUCLEOTIDES_QUERY}
          renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
          variables={{sequenceId}}
          fetchPolicy="cache-only"
        >
          {(data) => {
            const isOligo = data.componentSequence!.type === SequenceType.oligo;
            const modifications = data.componentSequence!.modifiedNucleotides;
            const nucleotideType = data.componentSequence!.nucleotideType;
            return (
              <ModifiedNucleotidesVisualization
                isOligo={isOligo}
                sequenceMods={modifications}
                monomers={monomerData.monomers}
                nucleotideType={nucleotideType}
                xFn={xFn}
                sliceStart={sliceStart}
                sliceEnd={sliceEnd}
                keyPrefix={sequenceId}
                bases={bases}
                layout={layout}
                boxHeightToDNA={boxHeightToDNA}
                showNucleotideStructures={showNucleotideStructures}
              />
            );
          }}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );
}
